function finiteNumber(value) {
  return Number.isFinite(value) ? value : undefined;
}

export function resolveEvalStoragePaths(evaluation, evalsDocData) {
  if (!evaluation?.id) return [];

  const candidates = [
    evalsDocData?.filename,
    evalsDocData?.organization_id
      ? `cloud_evals/${evalsDocData.organization_id}/${evaluation.id}.json`
      : null,
    evaluation.organization !== undefined && evaluation.organization !== null
      ? `cloud_evals/${evaluation.organization}/${evaluation.id}.json`
      : null,
  ];

  return candidates.filter((path, index) => (
    typeof path === 'string'
    && path.length > 0
    && candidates.indexOf(path) === index
  ));
}

export function readScreen(screenLike) {
  if (!screenLike || typeof screenLike !== 'object') return null;

  const width = finiteNumber(screenLike.Width ?? screenLike.width);
  const height = finiteNumber(screenLike.Height ?? screenLike.height);
  const trueWidth = finiteNumber(screenLike.TrueWidth ?? screenLike.trueWidth);
  const trueHeight = finiteNumber(screenLike.TrueHeight ?? screenLike.trueHeight);

  if (!trueWidth || trueWidth <= 0 || !trueHeight || trueHeight <= 0) return null;
  return { width, height, trueWidth, trueHeight };
}

export function isNewStyleEval(rawEval) {
  return rawEval != null && Object.prototype.hasOwnProperty.call(rawEval, 'SchemaVersion');
}

export function tileFractions(screen, isNewStyle) {
  // Rust scales 240x360 logical-point tiles only below its 1680x1080 minimum
  // surface. Legacy Blazor always drew 240x360 CSS pixels and clipped them, so
  // applying the Rust clamp to a real 1368x912 legacy eval would misplace tiles.
  const scale = isNewStyle
    ? Math.max(0, Math.min(1, screen.trueWidth / 1680, screen.trueHeight / 1080))
    : 1;

  return {
    width: (240 * scale) / screen.trueWidth,
    height: (360 * scale) / screen.trueHeight,
  };
}

export function classifyGazeEvents(gazeEvents, slideCount) {
  const safeSlideCount = Number.isInteger(slideCount) && slideCount > 0 ? slideCount : 0;
  const pointsBySlideIndex = Array.from({ length: safeSlideCount }, () => []);
  const invalidCountBySlideIndex = Array(safeSlideCount).fill(0);
  const events = Array.isArray(gazeEvents) ? gazeEvents : [];
  const markerEvents = events.filter((event) => {
    const isPoint = Number.isFinite(event?.X) && Number.isFinite(event?.Y);
    return !isPoint && Number.isFinite(event?.Slide);
  });
  const zeroBased = markerEvents.some((event) => event.Slide === 0);
  let currentSlideIndex = null;
  let unassignedPoints = 0;

  const toSlideIndex = (slide) => {
    if (!Number.isFinite(slide)) return null;
    const index = zeroBased ? slide : slide - 1;
    return Number.isInteger(index) && index >= 0 && index < safeSlideCount ? index : null;
  };

  events.forEach((event) => {
    const isPoint = Number.isFinite(event?.X) && Number.isFinite(event?.Y);
    const isMarker = !isPoint && Number.isFinite(event?.Slide);
    const isInvalid = !isPoint && event?.Invalid !== undefined;

    if (isPoint) {
      const pointSlideIndex = Number.isFinite(event.Slide)
        ? toSlideIndex(event.Slide)
        : currentSlideIndex;
      if (pointSlideIndex === null) {
        unassignedPoints += 1;
        return;
      }
      pointsBySlideIndex[pointSlideIndex].push({ x: event.X, y: event.Y, offset: event.Offset });
      return;
    }

    if (isMarker) {
      currentSlideIndex = toSlideIndex(event.Slide);
      return;
    }

    if (isInvalid && currentSlideIndex !== null) {
      invalidCountBySlideIndex[currentSlideIndex] += 1;
    }
  });

  return { pointsBySlideIndex, invalidCountBySlideIndex, unassignedPoints };
}

// Same colors and categories as the legacy viewer (view_eval/GazeHeatmapView.js),
// which is what clinicians read the point plot against.
export const GAZE_CATEGORY_COLORS = {
  face: '#22c55e',
  sexualized: '#ef4444',
  image: '#f59e0b',
  off: '#000000',
};

// Labels are authored in each stimulus's own 240x360 space.
const LABEL_SPACE = { width: 240, height: 360 };

export function stimulusHash(layoutKey) {
  return typeof layoutKey === 'string' ? layoutKey.split('/').pop().replace(/\.jpg$/i, '') : '';
}

function inLabel(x, y, label) {
  return Number.isFinite(label?.x1)
    && x >= label.x1 && x <= label.x2 && y >= label.y1 && y <= label.y2;
}

/**
 * Classify a normalized gaze point against the slide's tiles and AOI labels.
 * Face wins over sexualized regions, matching the legacy viewer.
 */
export function classifyGazePoint(point, layouts, tileSize, labelsByHash) {
  for (const layout of layouts ?? []) {
    const dx = point.x - layout.Left;
    const dy = point.y - layout.Top;
    if (dx < 0 || dy < 0 || dx > tileSize.width || dy > tileSize.height) continue;

    const labels = labelsByHash?.[stimulusHash(layout.Key)];
    if (labels) {
      const localX = (dx / tileSize.width) * LABEL_SPACE.width;
      const localY = (dy / tileSize.height) * LABEL_SPACE.height;
      if (inLabel(localX, localY, labels.FACE)) return 'face';
      if (inLabel(localX, localY, labels.SEX_1) || inLabel(localX, localY, labels.SEX_2)) return 'sexualized';
    }
    return 'image';
  }
  return 'off';
}

/**
 * Gaussian density of the points on a width x height grid, scaled so the
 * slide's densest cell is 1. Scaling to the peak keeps the map readable at any
 * sample count instead of saturating once a few points overlap.
 */
export function gazeDensity(points, width, height, sigma) {
  const density = new Float32Array(width * height);
  const reach = Math.ceil(sigma * 3);
  const kernel = Array.from({ length: reach * 2 + 1 }, (_, i) => Math.exp(-((i - reach) ** 2) / (2 * sigma * sigma)));

  points.forEach(({ x, y }) => {
    const cx = Math.round(x * (width - 1));
    const cy = Math.round(y * (height - 1));
    for (let gy = Math.max(0, cy - reach); gy <= Math.min(height - 1, cy + reach); gy += 1) {
      const wy = kernel[gy - cy + reach];
      for (let gx = Math.max(0, cx - reach); gx <= Math.min(width - 1, cx + reach); gx += 1) {
        density[gy * width + gx] += wy * kernel[gx - cx + reach];
      }
    }
  });

  let peak = 0;
  density.forEach((value) => { if (value > peak) peak = value; });
  if (peak > 0) density.forEach((value, index) => { density[index] = value / peak; });
  return density;
}
